import { forEach } from 'lodash';
import Store from 'lib/Store';
import Chat from 'entities/Chat';
// @ts-ignore
import ChatActions from 'chat/actions/ChatActions';
import ChatMemberStore from 'chat/stores/ChatMemberStore';
import ChatMessageStore from 'chat/stores/ChatMessageStore';
import Chats from 'collections/Chats';
import Message from 'entities/Message';
// @ts-ignore
import PotentialChatMember from 'entities/PotentialChatMember';
// @ts-ignore
import PotentialChatMembers from 'collections/PotentialChatMembers';
import { ActionTypes as AppActions } from 'constants/AppConstants';
import { ActionTypes as GroupActionTypes } from 'groups/constants/GroupConstants';
import { ActionTypes as LeadActions } from 'constants/LeadConstants';
import { ActionTypes as RelationshipActionTypes } from 'constants/RelationshipConstants';
import { ActionTypes, CHATS_PAGE_SIZE } from 'chat/constants/ChatConstants';
import {
  ChatMessage,
  chatMessageFactory,
  ChatGapMessage,
  shouldListenToPushEventFrom,
} from 'entities/ChatMessage';
import { PushEvents } from 'pusher/PusherConstants';
import { rootGroup } from 'entities/RootGroup';

import { MessageType } from 'chat/lib/MessageInterfacePropType';

import { Backbone$Id } from 'types/typescript/Backbone';

type GroupId = { id: string };

// Note: Flux stores are now a DEPRECATED pattern. See docs/GraphQL.md
class ChatStore extends Store {
  // TODO improve some of the filtering logic https://github.com/remind101/r101-frontend/pull/2327#issuecomment-200454662
  getInitialState() {
    return {
      chats: new Chats(),
      currentId: null,
      localChatInstance: null,
      members: new PotentialChatMembers(),
      invalidated: false,
      allMembersLoaded: false,
      focusView: null,
      // Includes chat list and search results
      chatsAndSearchedChats: new Chats(),
      loadedGroups: {}, // whether this group was specifically fetched
      allChatsLoadedForGroups: {},
    };
  }

  getChat(chatIdIsh?: Backbone$Id): Chat | undefined {
    const chatId = chatIdIsh ? chatIdIsh.toString() : null;
    return this.state.chats.get(chatId) || this.state.chatsAndSearchedChats.get(chatId);
  }

  getChats(group: GroupId & { className: string }): Chats {
    return (this.state.chats as Chats).selectChatsInGroup(group);
  }

  getChatsAndSearchedChats(group: GroupId & { className: string }): Chats {
    return (this.state.chatsAndSearchedChats as Chats).selectChatsInGroup(group);
  }

  getCurrentChat(): Chat | undefined | null {
    return this.state.currentId ? this.getChat(this.state.currentId) : null;
  }

  getCurrentChatId(): Backbone$Id | undefined {
    return this.state.currentId;
  }

  getGroupChats(): Chats {
    return this.state.chats.selectGroupChats();
  }

  getMembers(group?: GroupId): PotentialChatMembers {
    const id = group ? group.id : rootGroup.id;
    const { members } = this.state;
    if (id === rootGroup.id) {
      return this.state.members;
    } else {
      // TODO filter by PCMs who are in the group
      const memberModels = members.filter((m: PotentialChatMembers) => m.isMemberOfGroup(id));
      return new PotentialChatMembers(memberModels);
    }
  }

  getEligibleMemberModels(): Array<PotentialChatMember> {
    if (!this.state.eligibleMembers) {
      this.state.eligibleMembers = this.state.members.filter((member: PotentialChatMembers) =>
        member.isEligible()
      );
    }
    return this.state.eligibleMembers;
  }

  getFocusView() {
    return this.state.focusView;
  }

  areAllChatsLoaded(): boolean {
    return !!this.state.allChatsLoadedForGroups[rootGroup.id];
  }

  // Have we processed yet a response for a request for chats of this particular group
  haveChatsBeenLoadedForGroup(group: GroupId): boolean {
    return !!this.state.loadedGroups[group.id];
  }

  areAllChatsLoadedForGroup(group: GroupId): boolean {
    return this.areAllChatsLoaded() || !!this.state.allChatsLoadedForGroups[group.id];
  }

  areAllMembersLoaded(): boolean {
    return this.state.allMembersLoaded;
  }

  setAsRead(chatId: Backbone$Id): void {
    const chat = this.state.chats.get(chatId);
    if (chat) {
      chat.markAsRead();
    }
  }

  setPosition(chat: any): void {
    let position = 0;
    const firstChat = this.state.chats.first();
    if (!chat.isNew() && firstChat && firstChat.isNew()) {
      position = 1;
    }

    this.state.chats.setPosition(chat, position);
  }

  setLastMessageForChat(chatId: Backbone$Id, message: MessageType): void {
    const chat = this.state.chats.get(chatId);
    if (chat) {
      chat.setLastMessage(message);

      const updatedAt = chat.getUpdatedAt();
      if (
        typeof (message as Message).getCreatedAt === 'function' &&
        updatedAt < (message as Message).getCreatedAt()
      ) {
        chat.setUpdatedAt((message as Message).getCreatedAt());
        this.state.chats.sort();
      }
    }
  }

  setFocusView(focus: any): void {
    this.state.focusView = focus;
  }

  deactivate(chatId: Backbone$Id): void {
    const chat = this.state.chats.get(chatId);
    if (chat) {
      chat.deactivate();
    }
  }

  handleIncomingChatMessage(message: ChatMessage): void {
    const chatId = message.getChatId();

    const chat = this.state.chats.get(chatId);

    if (chat) {
      this.setLastMessageForChat(chatId, message);
      this.emitChange();
    } else {
      ChatActions.loadChat(chatId);
    }
  }

  clearLocalChat(): void {
    this.state.chats.remove(this.state.localChatInstance);
    this.state.localChatInstance = null;
  }

  loadLocalChat(members: any, title: string, originIds: any): void {
    this.clearLocalChat();
    this.state.localChatInstance = Chat.createLocal(members, title, originIds);
    this.addNewChat(this.state.localChatInstance);
  }

  hasLocal(): boolean {
    return !!this.state.localChatInstance;
  }

  getLocal() {
    return this.state.localChatInstance;
  }

  addNewChat(chat: Chat) {
    if (chat.isAssistantType()) {
      return;
    }

    // Refresh memberships from ChatMemberStore
    chat.getMemberships().forEach((membership: any) => {
      membership.setMember(ChatMemberStore.getMember(membership.getMember().id));
    });

    this.state.chats.add(chat);
    if (chat !== this.state.localChatInstance) {
      this.state.chatsAndSearchedChats.add(chat);
    }
  }

  shouldReloadChats(): boolean {
    return this.state.invalidated;
  }

  markTeacherLeadsInvited(leadIds: Backbone$Id[]) {
    leadIds.forEach(leadId => {
      const member = this.state.members.get(leadId);
      if (member) {
        member.setState('invited');
      }
    });
  }

  handleDispatch(action: any): void {
    this.waitFor([ChatMemberStore]);

    let chatTarget: Chat;

    switch (action.type) {
      case ActionTypes.RESET_STORE:
        this.resetState();
        break;
      case ActionTypes.LIST_CHATS:
        this.state.loadedGroups[action.groupId] = true;
        if (action.chats.length < CHATS_PAGE_SIZE) {
          this.state.allChatsLoadedForGroups[action.groupId] = true;
        }
        action.chats.map((chat: Chat) => this.addNewChat(chat));
        this.state.invalidated = false;
        this.emitChange();
        break;
      case ActionTypes.LOAD_CHAT:
        this.addNewChat(action.chat);
        this.emitChange();
        break;
      case ActionTypes.APPEND_CHATS: {
        const { groupId } = action;
        const loadedChatsCount = action.chats.length;

        if (loadedChatsCount < CHATS_PAGE_SIZE) {
          this.state.allChatsLoadedForGroups[groupId] = true;
        }

        action.chats.map((chat: Chat) => this.addNewChat(chat));
        this.state.chats.sort();
        this.emitChange();
        break;
      }
      case ActionTypes.SEARCH_CHATS:
        action.chats.forEach((chat: Chat) => this.state.chatsAndSearchedChats.add(chat));
        this.emitChange();
        break;
      case ActionTypes.CREATE_CHAT:
        this.addNewChat(action.chat);
        this.setPosition(action.chat);
        this.emitChange();
        break;
      case ActionTypes.LOAD_LOCAL_CHAT:
        this.loadLocalChat(action.members, action.title, action.originIds);
        this.setPosition(this.state.localChatInstance);
        this.emitChange();
        break;
      case ActionTypes.UPDATE_LOCAL_CHAT:
        this.emitChange();
        break;
      case ActionTypes.DESTROY_LOCAL_CHAT:
        this.clearLocalChat();
        this.emitChange();
        break;
      case ActionTypes.MARK_READ:
      case ActionTypes.READ_MESSAGE:
        this.setAsRead(action.chatId);
        this.emitChange();
        break;
      case ActionTypes.SELECT_CHAT:
        this.state.currentId = action.chatId;
        this.emitChange();
        break;
      case ActionTypes.RESET_SELECTION:
        this.state.currentId = null;
        this.emitChange();
        break;
      case GroupActionTypes.SEND_GROUP_MESSAGE: {
        this.emitChange();
        break;
      }
      case ActionTypes.SEND_MESSAGE:
        if (action.message instanceof ChatGapMessage) {
          console.error('attempt to deliver a gap message');
          break;
        }
        this.setLastMessageForChat(action.chatId, action.message);
        this.emitChange();
        break;
      case ActionTypes.EDIT_MEMBER_FROM_CHAT:
        chatTarget = this.state.chats.get(action.chat.id);
        chatTarget.addToMemberships(action.membership);
        this.emitChange();
        break;
      case ActionTypes.ADD_MEMBERS_TO_CHAT:
        chatTarget = this.state.chats.get(action.chat.id);
        action.memberships.forEach((membership: any) => {
          chatTarget.addToMemberships(membership);
        });
        this.emitChange();
        break;
      case ActionTypes.LOAD_MEMBERS:
        this.state.members.add(action.members.models);
        delete this.state.eligibleMembers; // invalidate cache
        this.state.allMembersLoaded = true;
        this.emitChange();
        break;
      case ActionTypes.UPDATE_CHAT:
        this.state.chats.set(action.chat, { remove: false });
        this.emitChange();
        break;
      case ActionTypes.DEACTIVATE:
        this.deactivate(action.chatId);
        this.emitChange();
        break;
      case RelationshipActionTypes.LOAD_RELATIONSHIP:
      case RelationshipActionTypes.UPDATE_RELATIONSHIP:
        if (action.relationship.getChat()) {
          this.addNewChat(action.relationship.getChat());
          this.emitChange();
        }
        break;
      case PushEvents.CHANGESET_CREATE_CHAT_MESSAGE:
        action.changes
          .map((change: any) => chatMessageFactory(change.content))
          .filter((message: any) => shouldListenToPushEventFrom(message))
          .forEach((message: any) => this.handleIncomingChatMessage(message));
        break;
      case PushEvents.CHANGESET_UPDATE_CHAT:
        // Refresh all our chats in case we missed a push.
        // The action sends `last_updated_ts` so we only fetch what's new.
        ChatActions.loadMoreChats(rootGroup);
        break;
      case PushEvents.CHANGESET_UPDATE_CHATS:
        ChatActions.resetPromiseMemoization();
        this.resetState();
        break;
      case AppActions.CONNECTION_STATE_CHANGED:
      case PushEvents.CONNECTION_STATE_CHANGED:
        if (action.current !== 'connected') {
          this.state.invalidated = true;
        }
        break;
      case LeadActions.INVITE_LEADS:
        this.markTeacherLeadsInvited(action.leadIds);
        this.emitChange();
        break;
      case ActionTypes.SET_FOCUS_VIEW:
        this.setFocusView(action.focus);
        break;
      default:
        break;
    }
  }
}

export default new ChatStore();
